import type { Edge, Node } from '@xyflow/react';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { BehaviorCaseNodeData, ParentCaseInfo, SegmentNodeData } from '../types/stepNodes';
import { getBehaviorCaseLabel } from './behaviorCaseLabels';

function getIncomingEdgesByTarget(edges: Edge[]): Map<string, Edge[]> {
	const incomingEdgesByTarget = new Map<string, Edge[]>();

	for (const edge of edges) {
		if (edge.type === 'phantomAddPath') {
			continue;
		}

		const existing = incomingEdgesByTarget.get(edge.target) || [];
		existing.push(edge);
		incomingEdgesByTarget.set(edge.target, existing);
	}

	return incomingEdgesByTarget;
}

function getSegmentLabel(segmentData: Partial<SegmentNodeData>): string {
	if (segmentData.isDefault) {
		return 'Default segment';
	}

	const explicitName = typeof segmentData.segmentName === 'string' ? segmentData.segmentName.trim() : '';
	if (explicitName.length > 0) {
		return `Segment "${explicitName}"`;
	}

	const ordinal =
		typeof segmentData.globalSegmentOrdinal === 'number'
			? segmentData.globalSegmentOrdinal
			: typeof segmentData.segmentIndex === 'number'
				? segmentData.segmentIndex + 1
				: undefined;

	return ordinal ? `Segment #${ordinal}` : 'Segment';
}

function getBehaviorCaseNodeLabel(behaviorCaseData: Partial<BehaviorCaseNodeData>): string {
	const explicitCaseName = typeof behaviorCaseData.caseName === 'string' ? behaviorCaseData.caseName.trim() : '';
	if (explicitCaseName.length > 0) {
		return explicitCaseName;
	}

	const explicitLabel = typeof behaviorCaseData.label === 'string' ? behaviorCaseData.label.trim() : '';
	if (explicitLabel.length > 0) {
		return explicitLabel;
	}

	return getBehaviorCaseLabel({
		id: typeof behaviorCaseData.caseId === 'string' ? behaviorCaseData.caseId : '',
		name: explicitCaseName,
		_type: behaviorCaseData.caseType,
		caseType: behaviorCaseData.caseType,
		engagementType: behaviorCaseData.engagementType,
		isDefault: behaviorCaseData.isDefault || behaviorCaseData.isTimeout,
	});
}

function getParentCaseInfoLabel(parentCaseInfo: ParentCaseInfo): string {
	if (parentCaseInfo.isBehaviorCase) {
		const explicitCaseName = typeof parentCaseInfo.caseName === 'string' ? parentCaseInfo.caseName.trim() : '';
		if (explicitCaseName.length > 0) {
			return explicitCaseName;
		}

		if (parentCaseInfo.isDefault) {
			return 'If no engagement (timeout)';
		}

		return `Behavior Case ${parentCaseInfo.caseIndex + 1}`;
	}

	if (parentCaseInfo.isDefault) {
		return 'Default segment';
	}

	const explicitSegmentName = typeof parentCaseInfo.caseName === 'string' ? parentCaseInfo.caseName.trim() : '';
	if (explicitSegmentName.length > 0) {
		return `Segment "${explicitSegmentName}"`;
	}

	const ordinal =
		typeof parentCaseInfo.globalSegmentOrdinal === 'number'
			? parentCaseInfo.globalSegmentOrdinal
			: parentCaseInfo.caseIndex + 1;
	return `Segment #${ordinal}`;
}

function getBranchBoundaryLabel(node: Node<BaseNodeData>): string | null {
	if (node.type === NODE_TYPES.SEGMENT) {
		return getSegmentLabel(node.data as Partial<SegmentNodeData>);
	}

	if (node.type === NODE_TYPES.BEHAVIOR_CASE) {
		return getBehaviorCaseNodeLabel(node.data as Partial<BehaviorCaseNodeData>);
	}

	return null;
}

export function findFlowNodeIdByStepId(nodes: Node<BaseNodeData>[], stepId: string): string | null {
	for (const node of nodes) {
		const data = node.data as { stepId?: unknown; automationStep?: { id?: unknown } };
		if (data.stepId === stepId || data.automationStep?.id === stepId) {
			return node.id;
		}
	}

	return null;
}

export function getPathNodeIdsToRoot(targetNodeId: string, edges: Edge[]): string[] {
	const incomingEdgesByTarget = getIncomingEdgesByTarget(edges);
	const pathNodeIds: string[] = [];
	const visitedNodeIds = new Set<string>();
	let currentNodeId: string | null = targetNodeId;

	while (currentNodeId && !visitedNodeIds.has(currentNodeId)) {
		pathNodeIds.unshift(currentNodeId);
		visitedNodeIds.add(currentNodeId);

		const incomingEdges = incomingEdgesByTarget.get(currentNodeId) || [];
		if (incomingEdges.length === 0) {
			break;
		}

		currentNodeId = incomingEdges[0].source;
	}

	return pathNodeIds;
}

export function getPathEdgeIds(pathNodeIds: string[], edges: Edge[]): string[] {
	if (pathNodeIds.length < 2) {
		return [];
	}

	const edgeIds: string[] = [];
	for (let index = 0; index < pathNodeIds.length - 1; index++) {
		const sourceId = pathNodeIds[index];
		const targetId = pathNodeIds[index + 1];
		const edge = edges.find(candidate => candidate.source === sourceId && candidate.target === targetId);
		if (edge) {
			edgeIds.push(edge.id);
		}
	}

	return edgeIds;
}

export function getBranchContextLabels(targetNodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): string[] {
	const nodeById = new Map(nodes.map(node => [node.id, node] as const));
	const pathNodeIds = getPathNodeIdsToRoot(targetNodeId, edges);
	const labels = pathNodeIds
		.map(nodeId => {
			const node = nodeById.get(nodeId);
			return node ? getBranchBoundaryLabel(node) : null;
		})
		.filter((label): label is string => typeof label === 'string' && label.length > 0);

	if (labels.length > 0) {
		return labels;
	}

	const targetNode = nodeById.get(targetNodeId);
	const parentCaseInfo = (targetNode?.data as { parentCaseInfo?: ParentCaseInfo } | undefined)?.parentCaseInfo;
	if (!parentCaseInfo) {
		return [];
	}

	return [getParentCaseInfoLabel(parentCaseInfo)];
}

export function getBranchContextLabel(targetNodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): string | null {
	const labels = getBranchContextLabels(targetNodeId, nodes, edges);
	return labels.length > 0 ? labels.join(' → ') : null;
}

export function getNodeBranchNestingDepth(targetNodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): number {
	return getBranchContextLabels(targetNodeId, nodes, edges).length;
}
